import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useStyles } from './SignoffNavigator.styles';
import { Box, Collapse } from '@mui/material';
import { MemberActions } from '../../../molecules/member-actions';
import { Sections, Subsections } from '../signoff.constants';
import { ProfileInfoRole } from '../../../../../components/v2/ProfileInfo';
import { Icons } from '../../../../icons';
import { colors } from '../../../../colors';

const SignoffNavigator = ({
  selectedSection,
  setSelectedSection,
  selectedSubSection,
  setSelectedSubSection,
  onMemberActionClick,
  practitionerDesignation,
  hasReportCard,
  appointment,
  isInteractiveMode = false,
  visibleSections = [],
}) => {
  const [expanded, setExpanded] = useState(true);
  const [availableSubsections, setAvailableSubsections] = useState([]);
  const classes = useStyles({ expanded });

  const handleSectionClick = (section) => {
    // Block navigation during interactive mode
    if (isInteractiveMode && section !== selectedSection) {
      return;
    }
    setSelectedSection(section);
  };

  // Determine available subsections based on practitioner designation
  useEffect(() => {
    let subsections = [Subsections.UPCOMING_APPOINTMENTS];

    // Only show diagnoses for Therapists and Nurse Practitioners
    if (
      practitionerDesignation === ProfileInfoRole.THERAPIST ||
      practitionerDesignation === ProfileInfoRole.NURSE_PRACTITIONER ||
      practitionerDesignation === ProfileInfoRole.PHYSICIAN
    ) {
      subsections.push(Subsections.REVIEW_DIAGNOSIS);
    }

    // Only show medications for Nurse Practitioners and Physicians
    if (
      practitionerDesignation === ProfileInfoRole.NURSE_PRACTITIONER ||
      practitionerDesignation === ProfileInfoRole.PHYSICIAN
    ) {
      subsections.push(Subsections.REVIEW_MEDICATIONS);
    }

    setAvailableSubsections(subsections);

    // If the current subsection is not in the available subsections, reset to the first available one
    if (
      selectedSection === Sections.FEEDBACK_FOR_US &&
      (!selectedSubSection || !subsections.includes(selectedSubSection))
    ) {
      setSelectedSubSection(subsections[0]);
    }
  }, [practitionerDesignation, selectedSubSection, setSelectedSubSection, selectedSection]);

  const isSectionCompleted = currentSection => {
    const currentIndex = visibleSections.indexOf(currentSection);
    const selectedIndex = visibleSections.indexOf(selectedSection);

    return selectedIndex > currentIndex;
  };

  const getSectionNumber = section => {
    return visibleSections.indexOf(section) + 1;
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.collapseArea}>
        <Box className={classes.collapseButton} onClick={() => setExpanded(!expanded)}>
          <Icons glyph="collapsable-outlined" />
        </Box>
      </Box>
      <Box className={classes.stepsContainer}>
        <Box
          className={clsx(classes.mainSection, {
            [classes.mainSectionSelected]: selectedSection === Sections.SESSION_NOTE,
            [classes.disabled]: isInteractiveMode && selectedSection !== Sections.SESSION_NOTE,
          })}
          onClick={() => handleSectionClick(Sections.SESSION_NOTE)}
        >
          <span
            className={clsx(classes.sectionCount, {
              [classes.sectionSelected]: selectedSection === Sections.SESSION_NOTE,
              [classes.sectionCompleted]: isSectionCompleted(Sections.SESSION_NOTE),
            })}
          >
            {isSectionCompleted(Sections.SESSION_NOTE) ? <Icons glyph="checkmark" color={colors.white} /> : getSectionNumber(Sections.SESSION_NOTE)}
          </span>
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]: selectedSection === Sections.SESSION_NOTE,
              })}
            >
              Session Note
            </span>
          )}
        </Box>
        {visibleSections.includes(Sections.TREATMENT_PLAN) && (
          <Box
            className={clsx(classes.mainSection, {
              [classes.mainSectionSelected]: selectedSection === Sections.TREATMENT_PLAN,
              [classes.disabled]: isInteractiveMode && selectedSection !== Sections.TREATMENT_PLAN,
            })}
            onClick={() => handleSectionClick(Sections.TREATMENT_PLAN)}
          >
            <span
              className={clsx(classes.sectionCount, {
                [classes.sectionSelected]: selectedSection === Sections.TREATMENT_PLAN,
                [classes.sectionCompleted]: isSectionCompleted(Sections.TREATMENT_PLAN),
              })}
            >
              {isSectionCompleted(Sections.TREATMENT_PLAN) ? <Icons glyph="checkmark" color={colors.white} /> : getSectionNumber(Sections.TREATMENT_PLAN)}
            </span>
            {expanded && (
              <span
                className={clsx(classes.sectionTitle, {
                  [classes.sectionTitleSelected]: selectedSection === Sections.TREATMENT_PLAN,
                })}
              >
                Treatment Plan
              </span>
            )}
          </Box>
        )}
        <Box
          className={clsx(classes.mainSection, {
            [classes.mainSectionSelected]: selectedSection === Sections.BILLING_REQUIREMENTS,
            [classes.disabled]: isInteractiveMode && selectedSection !== Sections.BILLING_REQUIREMENTS,
          })}
          onClick={() => handleSectionClick(Sections.BILLING_REQUIREMENTS)}
        >
          <span
            className={clsx(classes.sectionCount, {
              [classes.sectionSelected]: selectedSection === Sections.BILLING_REQUIREMENTS,
              [classes.sectionCompleted]: isSectionCompleted(Sections.BILLING_REQUIREMENTS),
            })}
          >
            {isSectionCompleted(Sections.BILLING_REQUIREMENTS) ? <Icons glyph="checkmark" color={colors.white} /> : getSectionNumber(Sections.BILLING_REQUIREMENTS)}
          </span>
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]: selectedSection === Sections.BILLING_REQUIREMENTS,
              })}
            >
              Billing Requirements
            </span>
          )}
        </Box>
        <Collapse in={selectedSection === Sections.BILLING_REQUIREMENTS}>
          {availableSubsections.includes(Subsections.REVIEW_DIAGNOSIS) && (
            <p
              className={clsx(classes.subsection, {
                [classes.subsectionSelected]: selectedSubSection === Subsections.REVIEW_DIAGNOSIS,
              })}
              onClick={() => {
                setSelectedSection(Sections.BILLING_REQUIREMENTS);
                setSelectedSubSection(Subsections.REVIEW_DIAGNOSIS);
              }}
            >
              {expanded ? 'Review Diagnoses' : '•'}
            </p>
          )}
          <p
            className={clsx(classes.subsection, {
              [classes.subsectionSelected]: selectedSubSection === Subsections.REVIEW_CPT_CODES,
            })}
            onClick={() => {
              setSelectedSection(Sections.BILLING_REQUIREMENTS);
              setSelectedSubSection(Subsections.REVIEW_CPT_CODES);
            }}
          >
            {expanded ? 'Review Procedure codes' : '•'}
          </p>
        </Collapse>
        <Box
          className={clsx(classes.mainSection, {
            [classes.mainSectionSelected]: selectedSection === Sections.FEEDBACK_FOR_US,
            [classes.disabled]: isInteractiveMode && selectedSection !== Sections.FEEDBACK_FOR_US,
          })}
          onClick={() => handleSectionClick(Sections.FEEDBACK_FOR_US)}
        >
          <span
            className={clsx(classes.sectionCount, {
              [classes.sectionSelected]: selectedSection === Sections.FEEDBACK_FOR_US,
              [classes.sectionCompleted]: isSectionCompleted(Sections.FEEDBACK_FOR_US),
            })}
          >
            {isSectionCompleted(Sections.FEEDBACK_FOR_US) ? <Icons glyph="checkmark" color={colors.white} /> : getSectionNumber(Sections.FEEDBACK_FOR_US)}
          </span>
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]: selectedSection === Sections.FEEDBACK_FOR_US,
              })}
            >
              Feedback for us
            </span>
          )}
        </Box>
        <Collapse in={selectedSection === Sections.FEEDBACK_FOR_US}>
          {/* Only show Medications for appropriate roles */}
          {availableSubsections.includes(Subsections.REVIEW_MEDICATIONS) && (
            <p
              className={clsx(classes.subsection, {
                [classes.subsectionSelected]: selectedSubSection === Subsections.REVIEW_MEDICATIONS,
              })}
              onClick={() => {
                setSelectedSection(Sections.FEEDBACK_FOR_US);
                setSelectedSubSection(Subsections.REVIEW_MEDICATIONS);
              }}
            >
              {expanded ? 'Review Medications' : '•'}
            </p>
          )}
          <p
            className={clsx(classes.subsection, {
              [classes.subsectionSelected]: selectedSubSection === Subsections.UPCOMING_APPOINTMENTS,
            })}
            onClick={() => {
              setSelectedSection(Sections.FEEDBACK_FOR_US);
              setSelectedSubSection(Subsections.UPCOMING_APPOINTMENTS);
            }}
          >
            {expanded ? 'Upcoming Appointments' : '•'}
          </p>
        </Collapse>
        <Box
          className={clsx(classes.mainSection, {
            [classes.mainSectionSelected]: selectedSection === Sections.FEEDBACK_FOR_YOU,
            [classes.disabled]: isInteractiveMode && selectedSection !== Sections.FEEDBACK_FOR_YOU,
          })}
          onClick={() => handleSectionClick(Sections.FEEDBACK_FOR_YOU)}
        >
          <span
            className={clsx(classes.sectionCount, {
              [classes.sectionSelected]: selectedSection === Sections.FEEDBACK_FOR_YOU,
              [classes.sectionCompleted]: isSectionCompleted(Sections.FEEDBACK_FOR_YOU),
            })}
          >
            {isSectionCompleted(Sections.FEEDBACK_FOR_YOU) ? <Icons glyph="checkmark" color={colors.white} /> : getSectionNumber(Sections.FEEDBACK_FOR_YOU)}
          </span>
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]: selectedSection === Sections.FEEDBACK_FOR_YOU,
              })}
            >
              Feedback for you
            </span>
          )}
        </Box>
        <Collapse in={selectedSection === Sections.FEEDBACK_FOR_YOU}>
          <p
            className={clsx(classes.subsection, {
              [classes.subsectionSelected]: selectedSubSection === Subsections.DETECTED_CASE_NOTES,
            })}
            onClick={() => {
              setSelectedSection(Sections.FEEDBACK_FOR_YOU);
              setSelectedSubSection(Subsections.DETECTED_CASE_NOTES);
            }}
          >
            {expanded ? 'Detected Case Notes' : '•'}
          </p>
          {/* <p
            className={clsx(classes.subsection, {
              [classes.subsectionSelected]: selectedSubSection === Subsections.DETECTED_SOCIAL_DETERMINANTS,
            })}
            onClick={() => {
              setSelectedSection(Sections.FEEDBACK_FOR_YOU);
              setSelectedSubSection(Subsections.DETECTED_SOCIAL_DETERMINANTS);
            }}
          >
            {expanded ? 'Detected Social Determinants' : '•'}
          </p>
          <p
            className={clsx(classes.subsection, {
              [classes.subsectionSelected]: selectedSubSection === Subsections.DETECTED_COGNITIVE_ISSUES,
            })}
            onClick={() => {
              setSelectedSection(Sections.FEEDBACK_FOR_YOU);
              setSelectedSubSection(Subsections.DETECTED_COGNITIVE_ISSUES);
            }}
          >
            {expanded ? 'Detected Cognitive Issues' : '•'}
          </p> */}
          {appointment?.sessionAnalysisAllowed && hasReportCard && (
            <p
              className={clsx(classes.subsection, {
                [classes.subsectionSelected]: selectedSubSection === Subsections.REPORT_CARD,
              })}
              onClick={() => {
                setSelectedSection(Sections.FEEDBACK_FOR_YOU);
                setSelectedSubSection(Subsections.REPORT_CARD);
              }}
            >
              {expanded ? 'Report Card' : '•'}
            </p>
          )}
        </Collapse>
      </Box>

      <MemberActions onMemberActionClick={onMemberActionClick} chatIconGlyph="chat" expanded={expanded} />
    </Box>
  );
};

export { SignoffNavigator };
